import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { tokens } from "../../public/tokens";
import { useDropdownPosition, useOutsideClick } from "./useDropdownBehavior";

const StyledTrigger = styled.button(({ disabled }) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.globals["spacing-1-5"],
    height: "35px",
    padding: `0 ${tokens.globals["spacing-2-5"]}`,
    background: tokens.colors.surfaces["container-background"],
    border: `${tokens.globals["spacing-px"]} solid ${tokens.colors.surfaces["border-divider"]}`,
    borderRadius: tokens.globals["border-radius"],
    boxShadow: tokens.effects.effects.shadow.default,
    color: tokens.colors.text.primary,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.semibold,
    whiteSpace: "nowrap",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,

    "i": { fontSize: tokens.typography.fontSize["md-12px"] },
    "&:hover:not(:disabled)": { borderColor: tokens.colors.brand.blue["500"] },
    "&:focus-visible": {
        outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: tokens.globals["spacing-0-5"],
    },
}));

const StyledMenu = styled.div(({ $top = 0, $left = 0 }) => ({
    position: "fixed",
    top: $top,
    left: $left,
    minWidth: "180px",
    display: "flex",
    flexDirection: "column",
    padding: tokens.globals["spacing-1"],
    background: tokens.colors.surfaces["container-background"],
    border: `${tokens.globals["spacing-px"]} solid ${tokens.colors.surfaces["border-divider"]}`,
    borderRadius: tokens.globals["border-radius"],
    boxShadow: tokens.effects.effects.shadow.hover,
    zIndex: 1600,
}));

const StyledMenuItem = styled.button({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-2-5"],
    padding: `${tokens.globals["spacing-2"]} ${tokens.globals["spacing-2-5"]}`,
    border: "none",
    background: "transparent",
    borderRadius: tokens.globals["border-radius"],
    color: tokens.colors.text.primary,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.semibold,
    textAlign: "left",
    whiteSpace: "nowrap",
    cursor: "pointer",

    "i": { fontSize: tokens.typography.fontSize["md-12px"], width: "14px" },
    "&:hover:not(:disabled)": { background: tokens.colors.neutral.grey.Alpha["10"] },
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
});

/**
 * @typedef {Object} DropdownOption
 * @property {string} key
 * @property {string} label
 * @property {string} icon Font Awesome icon name.
 * @property {Function} onClick
 */

/**
 * Labeled button that opens a small anchored menu of options (e.g. "Export" with format choices).
 * @param {string} [props.icon] Leading Font Awesome icon name shown before the label.
 * @param {DropdownOption[]} props.options Menu items, in display order.
 */
function DropdownButton({ icon, label, options = [], disabled = false, disabledReason, className }) {
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef(null);
    const listRef = useRef(null);
    const menuPosition = useDropdownPosition(triggerRef, isOpen);

    useOutsideClick(triggerRef, listRef, isOpen, () => setIsOpen(false));

    return (
        <>
            <StyledTrigger
                ref={triggerRef}
                className={className}
                type="button"
                disabled={disabled}
                title={disabled ? disabledReason : undefined}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                onClick={() => setIsOpen((open) => !open)}
            >
                {icon && <i className={`fal fa-${icon}`} aria-hidden="true" />}
                <span>{label}</span>
                <i className="fal fa-chevron-down" aria-hidden="true" />
            </StyledTrigger>

            {isOpen && createPortal(
                <StyledMenu ref={listRef} role="menu" $top={menuPosition.top} $left={menuPosition.left}>
                    {options.map((option) => (
                        <StyledMenuItem
                            key={option.key}
                            role="menuitem"
                            type="button"
                            disabled={option.disabled}
                            onClick={() => {
                                setIsOpen(false);
                                option.onClick();
                            }}
                        >
                            <i className={`fal fa-${option.icon}`} aria-hidden="true" />
                            <span>{option.label}</span>
                        </StyledMenuItem>
                    ))}
                </StyledMenu>,
                document.body
            )}
        </>
    );
}

DropdownButton.displayName = "DropdownButton";

export default DropdownButton;
